// src/components/ui/index.jsx  — REFACTORED v2
import React, { useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useTranslation } from 'react-i18next';

// ─── Button ──────────────────────────────────────────────────────────────────
export function Button({
  children, variant = 'primary', size = 'md',
  icon, loading = false, className = '', as: Tag, ...props
}) {
  const { t } = useTranslation();
  const base = 'inline-flex items-center justify-center gap-2 font-black uppercase tracking-[0.06em] rounded-2xl transition-all duration-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-40 select-none';
  const variants = {
    primary: 'bg-[#1e9f6b] text-white hover:bg-[#17875b] shadow-[0_10px_25px_rgba(30,159,107,0.30)] focus-visible:ring-[#1e9f6b]',
    secondary: 'bg-[var(--bg-subtle)] text-slate-700 dark:text-slate-200 hover:opacity-90',
    outline: 'border-2 border-[var(--border-strong)] text-[var(--fg2)] bg-transparent hover:bg-[var(--bg-subtle)] focus-visible:ring-[#1e9f6b]',
    danger: 'bg-red-500 text-white hover:bg-red-600 shadow-lg shadow-red-500/25 focus-visible:ring-red-500',
    ghost: 'text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800 normal-case tracking-normal font-bold',
    amber: 'bg-[#fbbf24] text-slate-900 hover:bg-amber-400 shadow-[0_10px_25px_rgba(251,191,36,0.3)]',
    blue: 'bg-[#3b82f6] text-white hover:bg-blue-600 shadow-[0_10px_25px_rgba(59,130,246,0.3)]',
    emerald: 'bg-emerald-500 text-white hover:bg-emerald-600 shadow-[0_10px_25px_rgba(16,185,129,0.3)]',
    green: 'bg-[#1e9f6b] text-white hover:bg-[#17875b] shadow-[0_10px_25px_rgba(30,159,107,0.30)]',
  };
  const sizes = {
    xs: 'h-9 px-4 text-sm', sm: 'h-11 px-5 text-base',
    md: 'h-14 px-7 text-lg', lg: 'h-16 px-9 text-xl', xl: 'h-[60px] px-10 text-xl w-full',
  };
  const MotionTag = Tag ? motion(Tag) : motion.button;
  return (
    <MotionTag
      whileHover={loading || props.disabled ? {} : { scale: 1.015 }}
      whileTap={loading || props.disabled ? {} : { scale: 0.97 }}
      transition={{ type: 'spring', stiffness: 400, damping: 25 }}
      className={`${base} ${variants[variant]} ${sizes[size]} ${className}`}
      disabled={!Tag && (loading || props.disabled)}
      {...props}
    >
      {loading ? (
        <span className="flex items-center gap-2">
          <span className="animate-spin h-5 w-5 border-2 border-current border-t-transparent rounded-full" />
          <span className="opacity-80">{t('common.loading')}</span>
        </span>
      ) : (
        <>
          {icon && <span className="material-symbols-outlined text-[22px] leading-none">{icon}</span>}
          {children}
        </>
      )}
    </MotionTag>
  );
}

// ─── Input ───────────────────────────────────────────────────────────────────
export function Input({ label, error, icon, rightIcon, onRightIconClick, className = '', hint, ...props }) {
  return (
    <div className="flex flex-col gap-1.5">
      {label && (
        <label className="sg-label text-[var(--slate-700)] dark:text-slate-300 pl-1">
          {label}
        </label>
      )}
      <div className="relative">
        {icon && (
          <span className="material-symbols-outlined absolute left-4 top-1/2 -translate-y-1/2 text-slate-400 text-[22px] pointer-events-none">
            {icon}
          </span>
        )}
        <input
          className={`w-full h-[58px] rounded-2xl border-2 border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 text-slate-900 dark:text-white placeholder:text-slate-400 dark:placeholder:text-slate-600 pr-5 text-lg transition-all duration-200 focus:outline-none focus:ring-4 focus:ring-[#1e9f6b]/20 focus:border-[#1e9f6b] ${icon ? 'pl-[52px]' : 'pl-5'} ${rightIcon ? 'pr-14' : ''} ${error ? 'border-red-400 focus:ring-red-400/20 focus:border-red-400' : ''} ${className}`}
          {...props}
        />
        {rightIcon && (
          <button type="button" onClick={onRightIconClick}
            className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-700 dark:hover:text-white transition-colors p-1">
            <span className="material-symbols-outlined text-[22px]">{rightIcon}</span>
          </button>
        )}
      </div>
      <AnimatePresence mode="wait">
        {error && (
          <motion.p initial={{ opacity: 0, y: -6 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -6 }}
            className="text-sm font-medium text-red-500 flex items-center gap-1.5">
            <span className="material-symbols-outlined text-[14px]">error</span>{error}
          </motion.p>
        )}
        {hint && !error && <p className="text-xs text-slate-400">{hint}</p>}
      </AnimatePresence>
    </div>
  );
}

// ─── OTP Input ───────────────────────────────────────────────────────────────
export function OtpInput({ value = '', onChange, length = 5, error }) {
  const inputs = useRef([]);
  const vals = value.split('').concat(Array(length).fill('')).slice(0, length);
  const handleChange = (idx, e) => {
    const char = e.target.value.replace(/\D/g, '').slice(-1);
    const next = [...vals]; next[idx] = char;
    onChange(next.join(''));
    if (char && idx < length - 1) inputs.current[idx + 1]?.focus();
  };
  const handleKeyDown = (idx, e) => {
    if (e.key === 'Backspace' && !vals[idx] && idx > 0) inputs.current[idx - 1]?.focus();
  };
  const handlePaste = (e) => {
    const paste = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, length);
    if (paste) onChange(paste.padEnd(length, '').slice(0, length));
  };
  return (
    <div className="flex flex-col gap-3">
      <div className="flex gap-1.5 sm:gap-2.5 justify-center w-full">
        {vals.map((v, i) => (
          <motion.input key={i} ref={el => inputs.current[i] = el}
            type="text" inputMode="numeric" maxLength={1} value={v}
            onChange={e => handleChange(i, e)} onKeyDown={e => handleKeyDown(i, e)} onPaste={handlePaste}
            whileFocus={{ scale: 1.05 }} transition={{ type: 'spring', stiffness: 400, damping: 25 }}
            className={`flex-1 w-full max-w-[50px] aspect-[4/5] sm:aspect-auto sm:h-[60px] text-center text-xl sm:text-2xl font-black rounded-xl border-2 bg-white dark:bg-slate-900 text-slate-900 dark:text-white outline-none transition-all duration-200 focus:border-[#1e9f6b] focus:ring-4 focus:ring-[#1e9f6b]/20 ${v ? 'border-[#1e9f6b] bg-[#1e9f6b]/5' : 'border-slate-200 dark:border-slate-700'} ${error ? 'border-red-400' : ''}`}
          />
        ))}
      </div>
      <AnimatePresence>
        {error && (
          <motion.p initial={{ opacity: 0, y: -6 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -6 }}
            className="text-sm font-medium text-red-500 text-center">{error}</motion.p>
        )}
      </AnimatePresence>
    </div>
  );
}

// ─── Card ────────────────────────────────────────────────────────────────────
export function Card({ children, className = '', hover = false, ...props }) {
  return (
    <motion.div
      whileHover={hover ? { y: -3, boxShadow: '0 16px 40px rgba(30,159,107,0.10)' } : {}}
      transition={{ duration: 0.2 }}
      className={`bg-[var(--bg-surface)] dark:bg-[var(--bg-surface)] rounded-3xl border border-[var(--border)] dark:border-[var(--border)] shadow-[var(--shadow-sm)] ${className}`}
      {...props}
    >
      {children}
    </motion.div>
  );
}

// ─── Badge ───────────────────────────────────────────────────────────────────
export function Badge({ children, variant = 'green', pulse = false, className = '' }) {
  const variants = {
    green: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400',
    blue: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400',
    amber: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400',
    red: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400',
    slate: 'bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-400',
    purple: 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400',
  };
  return (
    <span className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-black uppercase tracking-wider ${variants[variant]} ${className}`}>
      {pulse && (
        <span className="relative flex h-2 w-2">
          <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-current opacity-75" />
          <span className="relative inline-flex rounded-full h-2 w-2 bg-current" />
        </span>
      )}
      {children}
    </span>
  );
}

// ─── Spinner ─────────────────────────────────────────────────────────────────
export function Spinner({ size = 'md' }) {
  const sizes = { sm: 'h-5 w-5 border-2', md: 'h-9 w-9 border-[3px]', lg: 'h-14 w-14 border-[3px]' };
  return (
    <div className={`${sizes[size]} animate-spin rounded-full border-[#1e9f6b] border-t-transparent`} />
  );
}

// ─── Alert ───────────────────────────────────────────────────────────────────
export function Alert({ type = 'error', message }) {
  if (!message) return null;
  const styles = {
    error: 'bg-red-50 border-red-200 text-red-700 dark:bg-red-900/20 dark:border-red-800 dark:text-red-400',
    success: 'bg-emerald-50 border-emerald-200 text-emerald-700 dark:bg-emerald-900/20 dark:border-emerald-800 dark:text-emerald-400',
    info: 'bg-blue-50 border-blue-200 text-blue-700 dark:bg-blue-900/20 dark:border-blue-800 dark:text-blue-400',
  };
  const icons = { error: 'error', success: 'check_circle', info: 'info' };
  return (
    <motion.div initial={{ opacity: 0, y: -8 }} animate={{ opacity: 1, y: 0 }}
      className={`flex items-center gap-2.5 p-4 rounded-2xl border text-sm font-medium ${styles[type]}`}>
      <span className="material-symbols-outlined text-[20px] shrink-0">{icons[type]}</span>
      {message}
    </motion.div>
  );
}

// ─── StatusTimeline ──────────────────────────────────────────────────────────
export function StatusTimeline({ steps = [], currentStep = 0 }) {
  return (
    <div className="flex flex-col gap-0">
      {steps.map((step, idx) => {
        const done = idx < currentStep;
        const active = idx === currentStep;
        return (
          <div key={idx} className="flex gap-4 items-start">
            <div className="flex flex-col items-center">
              <motion.div initial={{ scale: 0.7 }} animate={{ scale: 1 }}
                className={`h-8 w-8 rounded-full flex items-center justify-center flex-shrink-0 transition-colors ${done ? 'bg-[#1e9f6b] text-white' : active ? 'bg-[#1e9f6b]/20 border-2 border-[#1e9f6b] text-[#1e9f6b]' : 'bg-slate-100 dark:bg-slate-800 text-slate-400'}`}>
                {done ? <span className="material-symbols-outlined text-[16px]">check</span>
                  : active ? <span className="h-2.5 w-2.5 rounded-full bg-[#1e9f6b] animate-pulse" />
                  : <span className="h-2 w-2 rounded-full bg-slate-300" />}
              </motion.div>
              {idx < steps.length - 1 && (
                <div className={`w-0.5 h-8 mt-0.5 rounded-full transition-colors ${done ? 'bg-[#1e9f6b]' : 'bg-slate-200 dark:bg-slate-700'}`} />
              )}
            </div>
            <div className={`pb-6 pt-1 ${idx === steps.length - 1 ? 'pb-0' : ''}`}>
              <p className={`text-sm font-bold transition-colors ${done || active ? 'text-slate-900 dark:text-white' : 'text-slate-400'}`}>{step.label}</p>
              {step.sub && <p className="text-xs text-slate-500 mt-0.5">{step.sub}</p>}
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ─── BottomNav ──────────────────────────────────────────────────────────────
export function BottomNav({ items = [], active }) {
  return (
    <nav className="fixed bottom-0 inset-x-0 z-50 bg-white dark:bg-slate-900 border-t border-slate-100 dark:border-slate-800 flex md:hidden safe-area-bottom">
      {items.map((item) => {
        const isActive = active === item.to;
        return (
          <a key={item.to} href={item.to}
            className={`flex-1 flex flex-col items-center justify-center gap-0.5 py-3 transition-colors ${isActive ? 'text-[#1e9f6b]' : 'text-slate-400'}`}>
            <span className={`material-symbols-outlined text-[26px] ${isActive ? 'text-[#1e9f6b]' : ''}`}>{item.icon}</span>
            <span className="text-[10px] font-bold">{item.label}</span>
            {isActive && (
              <motion.div layoutId="bottom-nav-dot" className="h-1 w-5 bg-[#1e9f6b] rounded-full mt-0.5" />
            )}
          </a>
        );
      })}
    </nav>
  );
}
// ─── Modal ───────────────────────────────────────────────────────────────────
export function Modal({ isOpen, onClose, title, children, maxWidth = 'max-w-2xl' }) {
  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
        {/* Backdrop */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={onClose}
          className="absolute inset-0 bg-slate-900/60 backdrop-blur-md"
        />

        {/* Content */}
        <motion.div
          initial={{ opacity: 0, scale: 0.9, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.9, y: 20 }}
          transition={{ type: 'spring', damping: 25, stiffness: 300 }}
          className={`relative w-full ${maxWidth} bg-white dark:bg-slate-900 rounded-[40px] shadow-2xl border border-slate-100 dark:border-slate-800 overflow-hidden flex flex-col max-h-[90vh]`}
        >
          {/* Header */}
          <div className="px-8 py-4 border-b border-slate-50 dark:border-slate-800/50 flex justify-between items-center bg-slate-50/10 dark:bg-slate-800/5">
            <div>
               {title && <h3 className="text-lg font-black italic uppercase tracking-tighter text-slate-900 dark:text-white leading-none">{title}</h3>}
            </div>
            <button 
              onClick={onClose}
              className="h-8 w-8 rounded-xl bg-slate-100 dark:bg-slate-800 flex items-center justify-center text-slate-400 hover:text-slate-900 dark:hover:text-white transition-all hover:scale-110 active:scale-95"
            >
              <span className="material-symbols-outlined text-[18px]">close</span>
            </button>
          </div>

          {/* Body */}
          <div className="flex-1 overflow-y-auto p-6 custom-scrollbar">
            {children}
          </div>
        </motion.div>
      </div>
    </AnimatePresence>
  );
}
